import { useEffect, useId, useRef, useState } from "react";
import { formatCurrency } from "../../utils/formatters";

function formatTaxRate(value) {
  const numericValue = Number(value || 0);

  return numericValue.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: numericValue % 1 === 0 ? 0 : 2,
  });
}

function SummaryLine({ label, value, strong = false }) {
  const Tag = strong ? "strong" : "span";

  return (
    <div className="summary-line">
      <span>{label}</span>
      <Tag>{formatCurrency(value)}</Tag>
    </div>
  );
}

function ServiceFeeLine({ description, value }) {
  const [isOpen, setIsOpen] = useState(false);
  const popoverId = useId();
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function handleDocumentPointerDown(event) {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleDocumentKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("pointerdown", handleDocumentPointerDown);
    document.addEventListener("keydown", handleDocumentKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handleDocumentPointerDown);
      document.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="summary-line summary-line--with-help" ref={wrapperRef}>
      <span className="summary-help-label">
        Service Fee
        <button
          aria-describedby={isOpen ? popoverId : undefined}
          aria-expanded={isOpen}
          aria-label="Why is there a service fee?"
          className="summary-info-button"
          type="button"
          onBlur={(event) => {
            if (!event.currentTarget.parentElement?.contains(event.relatedTarget)) {
              setIsOpen(false);
            }
          }}
          onClick={() => setIsOpen((currentValue) => !currentValue)}
          onFocus={() => setIsOpen(true)}
          onMouseEnter={() => setIsOpen(true)}
        >
          ?
        </button>
      </span>
      <strong>{formatCurrency(value)}</strong>

      {isOpen ? (
        <div className="summary-fee-popover" id={popoverId} role="tooltip" onMouseLeave={() => setIsOpen(false)}>
          <strong>Service Fee</strong>
          <p>{description}</p>
        </div>
      ) : null}
    </div>
  );
}

function CartSummary({ cartResult, loading, actionLabel = "Checkout", onAction, disabled }) {
  const subtotal = cartResult?.subtotal || 0;
  const discountAmount = cartResult?.discount_amount || cartResult?.discount || 0;
  const taxRatePercent = cartResult?.tax_rate_percent || 0;
  const taxAmount = cartResult?.tax_amount || 0;
  const deliveryFee = cartResult?.delivery_fee || 0;
  const serviceFee = cartResult?.service_fee || 0;
  const serviceFeeDescription = cartResult?.service_fee_description || "";
  const total = cartResult?.total || 0;
  const taxLabel = `Tax (${formatTaxRate(taxRatePercent)}%)`;

  return (
    <aside className="cart-summary" aria-busy={loading ? "true" : undefined}>
      <h2>Order summary</h2>
      <SummaryLine label="Subtotal" value={subtotal} />
      {Number(discountAmount) > 0 ? <SummaryLine label="Discount" value={-Number(discountAmount)} /> : null}
      <SummaryLine label={taxLabel} value={taxAmount} />
      {Number(deliveryFee) > 0 ? <SummaryLine label="Delivery fee" value={deliveryFee} /> : null}
      {Number(serviceFee) > 0 ? <ServiceFeeLine description={serviceFeeDescription} value={serviceFee} /> : null}
      <SummaryLine label="Total" value={total} strong />
      <button className="btn btn-primary btn-block" disabled={disabled || loading} onClick={onAction} type="button">
        {loading ? "Checking cart" : actionLabel}
      </button>
    </aside>
  );
}

export default CartSummary;
